// src/design/PublishPreviewModal.jsx
// Preview modal — shows what the post will look like on each platform
// with per-platform image aspect ratios and realistic frame styling.
// Zero AI cost — purely CSS/JSX.

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

// ─── Platform definitions ────────────────────────────────────────────────────
const PLATFORMS = [
  {
    key: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    key: 'instagram',
    label: 'Instagram',
    color: '#E4405F',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    color: '#0A66C2',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    color: '#FF0000',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    key: 'twitter',
    label: 'Twitter / X',
    color: '#000000',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    color: '#000000',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
];

// ─── Helper: make a handle from business name ───────────────────────────────
function makeHandle(name) {
  if (!name) return 'yourbusiness';
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 20);
}

// ─── Helper: initials from business name ────────────────────────────────────
function makeInitials(name) {
  if (!name) return 'Biz';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ─── Media composite (image or video + headline overlay) ──────────
// When `editable` is true the headline box becomes draggable — mousedown on
// the headline and drag to reposition. onHeadlineMove({x, y}) fires with the
// new percent-based position while dragging.
function ImageComposite({ src, position, zoom, headline, headlinePos, headlineStyle, aspect, assetType, isVertical, editable, onHeadlineMove }) {
  const containerRef = useRef(null);
  const dragStateRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered]   = useState(false);
  const [containerW, setContainerW] = useState(340);
  const color = headlineStyle?.color ?? '#FFFFFF';
  const bgOpacity = headlineStyle?.bgOpacity ?? 0.6;
  const boxWidth = headlineStyle?.boxWidth ?? 60;

  // User's fontSize is the reference size at a 340px container. Preview frames
  // have different widths (TikTok ~300, FB ~420, YouTube ~720), so scale
  // proportionally — this preserves the user's ± size adjustment across every
  // platform preview. Default 14 × 340 / 340 = 14, matching the prior heuristic.
  const scaledFontSize = Math.max(
    8,
    Math.round(((headlineStyle?.fontSize ?? 14) * containerW) / 340),
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerW(entry.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Drag handlers for the editable headline overlay
  const handleHeadlineMouseDown = (e) => {
    if (!editable || !onHeadlineMove) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      rect,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startPosX: headlinePos?.x ?? 50,
      startPosY: headlinePos?.y ?? 85,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const dx = ((e.clientX - ds.startClientX) / ds.rect.width)  * 100;
      const dy = ((e.clientY - ds.startClientY) / ds.rect.height) * 100;
      const newX = Math.max(0, Math.min(100, ds.startPosX + dx));
      const newY = Math.max(0, Math.min(100, ds.startPosY + dy));
      onHeadlineMove?.({ x: newX, y: newY });
    };
    const handleUp = () => {
      setIsDragging(false);
      dragStateRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging, onHeadlineMove]);

  const isLightColor = ['#FFFFFF', '#FFD700', '#84CC16', '#F87171', '#F97316'].includes(color);
  const bgColor = isLightColor
    ? `rgba(0,0,0,${bgOpacity})`
    : `rgba(255,255,255,${bgOpacity})`;
  const textShadow = isLightColor
    ? '0 1px 4px rgba(0,0,0,0.7)'
    : '0 1px 4px rgba(255,255,255,0.5)';

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden bg-gray-100"
      style={{ aspectRatio: aspect }}
    >
      {assetType === 'video' ? (
        <video
          key={src}
          src={src}
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          controlsList="nodownload noplaybackrate nofullscreen"
          className="w-full h-full object-cover pointer-events-none"
          preload="metadata"
        />
      ) : (
        <img
          src={src}
          alt=""
          draggable={false}
          className="pointer-events-none"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: `${position.x}% ${position.y}%`,
            transform: `scale(${zoom})`,
            transformOrigin: `${position.x}% ${position.y}%`,
          }}
        />
      )}

      {headline && (
        <div
          className={`absolute ${editable ? '' : 'pointer-events-none'}`}
          onMouseDown={handleHeadlineMouseDown}
          onMouseEnter={() => editable && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            left: `${headlinePos?.x ?? 50}%`,
            top: `${headlinePos?.y ?? 85}%`,
            transform: 'translate(-50%, -50%)',
            width: `${boxWidth}%`,
            maxWidth: '95%',
            cursor: editable ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            touchAction: editable ? 'none' : 'auto',
          }}
          title={editable ? 'Drag to reposition the headline' : undefined}
        >
          <div
            className="px-3 py-1.5 rounded"
            style={{
              background: bgOpacity > 0 ? bgColor : 'transparent',
              backdropFilter: bgOpacity > 0 ? 'blur(4px)' : 'none',
              boxShadow: editable && (isHovered || isDragging)
                ? '0 0 0 3px rgba(0,174,247,0.35)'
                : 'none',
              transition: 'box-shadow 150ms ease',
            }}
          >
            <span
              className="font-bold leading-tight block text-center"
              style={{
                fontSize: `${scaledFontSize}px`,
                color,
                textShadow,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {headline}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Facebook Frame ─────────────────────────────────────────────────────────
function FacebookFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">Just now · 🌐</div>
        </div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="10" cy="16" r="2"/></svg>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="1.91/1" assetType={assetType} editable={editable} onHeadlineMove={onHeadlineMove} />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5">
          <span className="text-[12px]">👍</span>
          <span className="text-[12px]">❤️</span>
          <span>24</span>
        </div>
        <div>3 comments · 2 shares</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <button className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">
          <span className="text-[12px]">👍</span> Like
        </button>
        <button className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">
          <span className="text-[12px]">💬</span> Comment
        </button>
        <button className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">
          <span className="text-[12px]">↗️</span> Share
        </button>
      </div>
    </div>
  );
}

// ─── Instagram Frame ────────────────────────────────────────────────────────
function InstagramFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  // Explicit w-[300px] (not just max-w) — the 1:1 ImageComposite inside uses
  // w-full, so without an explicit frame width the image sizing propagates
  // unpredictably and the parent scroll container misbehaves.
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-[300px] max-w-full mx-auto flex-shrink-0">
      <div className="px-2.5 py-1.5 flex items-center gap-2 border-b border-gray-100">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[8px] font-bold text-gray-700">{biz.initials}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.handle}</div>
        </div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="4" cy="10" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="16" cy="10" r="2"/></svg>
      </div>
      <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="1/1" assetType={assetType} editable={editable} onHeadlineMove={onHeadlineMove} />
      <div className="px-2.5 py-1.5 flex items-center gap-3">
        <span className="text-[15px] cursor-pointer">🤍</span>
        <span className="text-[15px] cursor-pointer">💬</span>
        <span className="text-[15px] cursor-pointer">📤</span>
        <span className="ml-auto text-[15px] cursor-pointer">🔖</span>
      </div>
      <div className="px-2.5 text-[11px] font-semibold text-gray-900">127 likes</div>
      <div className="px-2.5 pb-2.5 pt-0.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">
          <span className="font-semibold">{biz.handle} </span>{caption}
        </p>
      </div>
    </div>
  );
}

// ─── LinkedIn Frame ─────────────────────────────────────────────────────────
function LinkedInFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">{biz.location || 'Local Business'}</div>
          <div className="text-[10px] text-gray-400">Just now · 🌐</div>
        </div>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="1.91/1" assetType={assetType} editable={editable} onHeadlineMove={onHeadlineMove} />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5">
          <span className="text-[12px]">👍</span><span className="text-[12px]">💡</span><span className="text-[12px]">❤️</span>
          <span className="ml-1">48</span>
        </div>
        <div>6 comments</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <button className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">👍 Like</button>
        <button className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">💬 Comment</button>
        <button className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">🔄 Repost</button>
        <button className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5 rounded hover:bg-gray-50">📤 Send</button>
      </div>
    </div>
  );
}

// ─── Twitter/X Frame ────────────────────────────────────────────────────────
function TwitterFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1 flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-[9px] font-bold shrink-0">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-bold text-gray-900">{biz.name}</span>
            <span className="text-[10px] text-gray-500">@{biz.handle} · Just now</span>
          </div>
          <p className="text-[11px] text-gray-900 leading-snug whitespace-pre-wrap mt-0.5">{caption}</p>
        </div>
      </div>
      <div className="px-2.5 pb-1">
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="16/9" assetType={assetType} editable={editable} onHeadlineMove={onHeadlineMove} />
        </div>
      </div>
      <div className="px-8 py-1.5 flex items-center justify-between text-gray-500 text-[11px]">
        <span className="flex items-center gap-0.5 cursor-pointer hover:text-blue-500">💬 <span>5</span></span>
        <span className="flex items-center gap-0.5 cursor-pointer hover:text-green-500">🔄 <span>12</span></span>
        <span className="flex items-center gap-0.5 cursor-pointer hover:text-red-500">🤍 <span>47</span></span>
        <span className="flex items-center gap-0.5 cursor-pointer hover:text-blue-500">📤</span>
      </div>
    </div>
  );
}

// ─── TikTok Frame ───────────────────────────────────────────────────────────
function TikTokFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  // 9:16 locked with explicit width+height. aspect-ratio alone gets
  // overridden by the flex-column parent when the derived height
  // exceeds the available preview space.
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden mx-auto flex-shrink-0" style={{ width: '300px', height: '533px' }}>
      <div className="relative w-full h-full">
        <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="9/16" assetType={assetType} isVertical editable={editable} onHeadlineMove={onHeadlineMove} />
        {/* Decorative TikTok chrome — must be pointer-events-none so it doesn't
            intercept clicks meant for the headline drag handle inside ImageComposite. */}
        <div className="absolute bottom-0 left-0 right-0 p-2 pointer-events-none" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3 pointer-events-none">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">🤍</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">💬</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">📤</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── YouTube Frame ─────────────────────────────────────────────────────────
function YouTubeFrame({ src, position, zoom, headline, headlinePos, headlineStyle, caption, biz, assetType, editable, onHeadlineMove }) {
  // See TikTokFrame — 9:16 locked with explicit dimensions.
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden mx-auto flex-shrink-0" style={{ width: '300px', height: '533px' }}>
      <div className="relative w-full h-full">
        <ImageComposite src={src} position={position} zoom={zoom} headline={headline} headlinePos={headlinePos} headlineStyle={headlineStyle} aspect="9/16" assetType={assetType} isVertical editable={editable} onHeadlineMove={onHeadlineMove} />
        {/* Decorative YouTube Shorts chrome — pointer-events-none so it doesn't
            intercept clicks meant for the headline drag handle inside ImageComposite. */}
        <div className="absolute bottom-0 left-0 right-0 p-2 pointer-events-none" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3 pointer-events-none">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128077;</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128078;</div>
            <span className="text-white text-[8px] mt-0.5">Dislike</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128172;</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128228;</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
        {/* YouTube Shorts badge — decorative */}
        <div className="absolute top-2 left-2 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded pointer-events-none">Shorts</div>
      </div>
    </div>
  );
}

// ─── Frame dispatcher ───────────────────────────────────────────────────────
export const CREATION_FRAME_MAP = {
  facebook: FacebookFrame,
  instagram: InstagramFrame,
  linkedin: LinkedInFrame,
  youtube: YouTubeFrame,
  twitter: TwitterFrame,
  tiktok: TikTokFrame,
};

// ─── Main Modal ─────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || '';

export default function PublishPreviewModal({
  imageSrc,
  imageAssetId,
  assetType,
  existingTiktokVideoAssetId,
  position,
  zoom,
  headline,
  headlinePos,
  headlineStyle,
  captions,
  businessInfo,
  professionName,
  onClose,
  onPublish,
}) {
  const [activePlatform, setActivePlatform] = useState('facebook');

  // ── Per-platform caption edits (local to this modal session) ──
  // Initialize from props. Edits are passed back via onPublish — nothing
  // persists if the user closes the modal without publishing.
  const [editedCaptions, setEditedCaptions] = useState(() => ({ ...(captions || {}) }));

  // ── Per-platform headline overlay overrides ──
  // Empty by default — each platform falls back to the global headlineStyle/headlinePos
  // unless the user has moved/edited the overlay for that specific platform.
  const [editedOverlays, setEditedOverlays] = useState({});

  // Compute the effective overlay position/style for the active platform
  const activeOverlay = editedOverlays[activePlatform] || null;
  const effectiveHeadlinePos   = activeOverlay
    ? { x: activeOverlay.posX ?? headlinePos?.x ?? 50, y: activeOverlay.posY ?? headlinePos?.y ?? 71 }
    : headlinePos;
  const effectiveHeadlineStyle = activeOverlay
    ? { ...headlineStyle, ...activeOverlay }
    : headlineStyle;

  // ── TikTok/YouTube video gate state ──
  const isImageAsset = assetType !== 'video';
  const hasExistingVideo = !!existingTiktokVideoAssetId;
  const [tiktokVideoAssetId, setTiktokVideoAssetId] = useState(existingTiktokVideoAssetId || null);
  const [tiktokVideoUrl, setTiktokVideoUrl] = useState(null);
  const [tiktokCaptioned, setTiktokCaptioned] = useState(false);
  const [tiktokUseGenerated, setTiktokUseGenerated] = useState(hasExistingVideo);
  const [tiktokGenerating, setTiktokGenerating] = useState(false);
  const [tiktokUploading, setTiktokUploading] = useState(false);
  const [tiktokError, setTiktokError] = useState('');
  const [tiktokGateVisible, setTiktokGateVisible] = useState(!hasExistingVideo);
  const tiktokHideTimer = useRef(null);

  // If hero is already a video, auto-set tiktok asset = hero asset
  const effectiveTiktokAssetId = isImageAsset ? tiktokVideoAssetId : imageAssetId;

  // On re-open with existing video asset, fetch the video URL so preview plays as video
  useEffect(() => {
    if (!hasExistingVideo || !existingTiktokVideoAssetId || tiktokVideoUrl) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/asset-url/${existingTiktokVideoAssetId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok && data.video_url) {
          setTiktokVideoUrl(data.video_url + `?t=${Date.now()}`);
          setTiktokCaptioned(true); // assume captioned since it was previously generated
        }
      } catch { /* silent */ }
    })();
  }, [hasExistingVideo, existingTiktokVideoAssetId, tiktokVideoUrl]);

  // Auto-hide TikTok gate 2s after video is ready
  useEffect(() => {
    if (tiktokVideoAssetId && !tiktokGenerating && !tiktokUploading) {
      tiktokHideTimer.current = setTimeout(() => setTiktokGateVisible(false), 2000);
    } else {
      setTiktokGateVisible(true);
    }
    return () => clearTimeout(tiktokHideTimer.current);
  }, [tiktokVideoAssetId, tiktokGenerating, tiktokUploading]);

  // Poll for captioned video (background pass 2) when raw Ken Burns is ready
  useEffect(() => {
    if (!tiktokVideoAssetId || tiktokCaptioned) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/tiktok-video-status/${tiktokVideoAssetId}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (!cancelled && data.ok && data.captioned) {
          // Force video reload with cache-buster (pass 2 overwrote the same file)
          setTiktokVideoUrl(prev => prev ? prev.split('?')[0] + `?t=${Date.now()}` : prev);
          setTiktokCaptioned(true);
        }
      } catch (e) { /* silent */ }
    };
    const interval = setInterval(() => {
      if (cancelled || attempts >= 20) { clearInterval(interval); return; }
      attempts++;
      poll();
    }, 3000);
    poll(); // immediate first check
    return () => { cancelled = true; clearInterval(interval); };
  }, [tiktokVideoAssetId, tiktokCaptioned]);

  // Generate Ken Burns video on checkbox check
  const handleGenerateKenBurns = useCallback(async () => {
    if (!isImageAsset || !imageAssetId) return;
    setTiktokGenerating(true);
    setTiktokError('');
    setTiktokCaptioned(false); // reset so polling restarts for new video
    try {
      const res = await fetch(`${API_URL}/api/designcenter/generate-tiktok-video`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageAssetId,
          headline,
          headlineStyleJson: { ...headlineStyle, posX: headlinePos?.x, posY: headlinePos?.y },
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setTiktokVideoAssetId(data.asset_id);
        if (data.video_url) setTiktokVideoUrl(data.video_url);
      } else {
        setTiktokError(data.error || 'Failed to generate video');
        setTiktokUseGenerated(false);
      }
    } catch (err) {
      setTiktokError('Failed to generate video. Please try again.');
      setTiktokUseGenerated(false);
    }
    setTiktokGenerating(false);
  }, [imageAssetId, headline, headlineStyle, headlinePos, isImageAsset]);

  // Handle user uploading their own TikTok video
  const handleTiktokUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTiktokUploading(true);
    setTiktokError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subcategory', 'tiktok_upload');
      const res = await fetch(`${API_URL}/api/designcenter/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (data.ok || data.asset_id) {
        setTiktokVideoAssetId(data.asset_id);
        setTiktokUseGenerated(false); // they uploaded their own
      } else {
        setTiktokError(data.error || 'Upload failed');
      }
    } catch (err) {
      setTiktokError('Upload failed. Please try again.');
    }
    setTiktokUploading(false);
  }, []);

  // Publish is always allowed — warn if no video for TikTok/YouTube
  const [showNoVideoWarning, setShowNoVideoWarning] = useState(false);

  const FrameComponent = CREATION_FRAME_MAP[activePlatform];
  const captionText = editedCaptions?.[activePlatform] ?? '';
  const isTwitter = activePlatform === 'twitter';
  const twitterCharCount = captionText.length;
  const TWITTER_MAX = 280;

  // Build business display data + tags
  const biz = useMemo(() => {
    const name = businessInfo?.name || 'Your Business';
    const city = businessInfo?.city || '';
    const state = businessInfo?.state || '';
    const handle = makeHandle(name);
    const initials = makeInitials(name);
    const location = [city, state].filter(Boolean).join(', ');

    return { name, handle, initials, location };
  }, [businessInfo, professionName]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onClose(tiktokVideoAssetId)}
      />

      {/* Modal card */}
      <div className="relative bg-gray-50 rounded-2xl shadow-2xl flex flex-col h-[80vh] w-[420px] max-w-[90vw] overflow-hidden">

        {/* ─── Platform icon tabs — icons only, hover shadow ─── */}
        <div className="shrink-0 bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-center gap-3">
          {PLATFORMS.map((p) => {
            const isActive = p.key === activePlatform;
            const isVideoPlat = p.key === 'tiktok' || p.key === 'youtube';
            const hasVideo = !!effectiveTiktokAssetId;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setActivePlatform(p.key)}
                className={`relative w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  isActive
                    ? 'shadow-md bg-gray-50'
                    : 'hover:shadow-md hover:bg-gray-50'
                }`}
                title={p.label}
              >
                <span
                  className="[&>svg]:w-5 [&>svg]:h-5"
                  style={{ color: p.color }}
                >
                  {p.icon}
                </span>
                {/* Green checkmark badge for video platforms when Ken Burns is ready */}
                {isVideoPlat && isImageAsset && hasVideo && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ─── Preview area — thin gray scrollbar, bottom padding so tags aren't cut off ─── */}
        <div className="flex-1 min-h-0 overflow-auto p-3 pb-[20px] flex flex-col items-center gap-2 bg-gray-100 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {/* Edit position button — appears above a generated TikTok/YouTube video so
              the user can swap to image mode and drag the headline to a new spot */}
          {FrameComponent && (activePlatform === 'tiktok' || activePlatform === 'youtube') && tiktokVideoUrl && (
            <button
              type="button"
              onClick={() => {
                // Swap back to image view so the overlay is visible and draggable.
                // Clear video state so the Ken Burns gate reappears and the user
                // can regenerate with the new position after dragging.
                setTiktokVideoAssetId(null);
                setTiktokVideoUrl(null);
                setTiktokUseGenerated(false);
                setTiktokCaptioned(false);
                setTiktokGateVisible(true);
              }}
              className="px-3 py-1.5 text-[11px] font-medium text-[#00aef7] bg-white border border-[#00aef7] rounded hover:bg-[#00aef7]/5 flex items-center gap-1.5"
              title="Move the headline to a new position — will require regenerating the video"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
              </svg>
              Edit headline position
            </button>
          )}
          {FrameComponent && (() => {
            // If we're showing a generated Ken Burns video for TikTok/YouTube, the
            // headline is baked in — don't allow drag-reposition (user must regenerate).
            // Otherwise allow drag to reposition per-platform.
            const showingVideo = (activePlatform === 'tiktok' || activePlatform === 'youtube') && tiktokVideoUrl;
            const allowDrag = !showingVideo;
            const handleHeadlineMove = (newPos) => {
              setEditedOverlays(prev => ({
                ...prev,
                [activePlatform]: {
                  ...(prev[activePlatform] || {}),
                  posX: newPos.x,
                  posY: newPos.y,
                },
              }));
              // A.5: if this is TikTok/YouTube AND we have a generated video,
              // the overlay position is baked into the video — invalidate it so
              // the user regenerates.
              if ((activePlatform === 'tiktok' || activePlatform === 'youtube') && tiktokVideoAssetId) {
                setTiktokVideoAssetId(null);
                setTiktokVideoUrl(null);
                setTiktokUseGenerated(false);
                setTiktokCaptioned(false);
                setTiktokGateVisible(true);
              }
            };
            return (
              <FrameComponent
                src={showingVideo ? tiktokVideoUrl : imageSrc}
                assetType={showingVideo ? 'video' : assetType}
                position={position}
                zoom={zoom}
                headline={showingVideo && tiktokCaptioned ? '' : headline}
                headlinePos={effectiveHeadlinePos}
                headlineStyle={effectiveHeadlineStyle}
                caption={captionText}
                biz={biz}
                editable={allowDrag}
                onHeadlineMove={handleHeadlineMove}
              />
            );
          })()}
        </div>

        {/* ─── Editable caption textarea (local to modal) ─── */}
        <div className="shrink-0 bg-white border-t border-gray-200 px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
              {activePlatform === 'twitter' ? 'X' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)} caption
            </label>
            <div className="flex items-center gap-2">
              {isTwitter && (
                <span className={`text-[10px] font-medium ${twitterCharCount > TWITTER_MAX ? 'text-red-500' : twitterCharCount > TWITTER_MAX * 0.9 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {twitterCharCount}/{TWITTER_MAX}
                </span>
              )}
              {captions?.[activePlatform] && editedCaptions[activePlatform] !== captions[activePlatform] && (
                <button
                  type="button"
                  onClick={() => setEditedCaptions(prev => ({ ...prev, [activePlatform]: captions[activePlatform] }))}
                  className="text-[10px] text-[#00aef7] hover:underline"
                >
                  Reset caption
                </button>
              )}
              {editedOverlays[activePlatform] && (
                <button
                  type="button"
                  onClick={() => setEditedOverlays(prev => {
                    const next = { ...prev };
                    delete next[activePlatform];
                    return next;
                  })}
                  className="text-[10px] text-[#00aef7] hover:underline"
                >
                  Reset position
                </button>
              )}
            </div>
          </div>
          <textarea
            value={captionText}
            onChange={(e) => setEditedCaptions(prev => ({ ...prev, [activePlatform]: e.target.value }))}
            placeholder={`Write your ${activePlatform === 'twitter' ? 'X' : activePlatform} caption...`}
            rows={3}
            className={`w-full px-2 py-1.5 text-[12px] text-gray-800 rounded border resize-none focus:outline-none focus:ring-1 ${
              isTwitter && twitterCharCount > TWITTER_MAX
                ? 'border-red-300 focus:border-red-400 focus:ring-red-300'
                : 'border-gray-300 focus:border-[#00aef7] focus:ring-[#00aef7]'
            }`}
          />
        </div>

        {/* ─── TikTok/YouTube Video Gate (only for image assets on video platforms) ─── */}
        {isImageAsset && tiktokGateVisible && (activePlatform === 'tiktok' || activePlatform === 'youtube') && (
          <div className="shrink-0 bg-amber-50 border-t border-amber-200 px-3 py-2.5">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-800 mb-1">TikTok and YouTube require a video</p>
                <p className="text-[11px] text-amber-700 mb-2">
                  We can create a video from the image you chose for TikTok and YouTube. Or you can use your own video instead.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="px-2.5 py-1 rounded bg-[#00aef7] text-white text-[11px] font-medium cursor-pointer hover:bg-[#0097d6] transition-colors">
                    Upload My Video
                    <input type="file" accept="video/*" className="hidden" onChange={handleTiktokUpload} disabled={tiktokUploading} />
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tiktokUseGenerated}
                      onChange={(e) => {
                        setTiktokUseGenerated(e.target.checked);
                        if (e.target.checked && !tiktokVideoAssetId) {
                          handleGenerateKenBurns();
                        }
                      }}
                      disabled={tiktokGenerating}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-[#00aef7] focus:ring-[#00aef7]"
                    />
                    <span className="text-[11px] text-amber-800">Use the video created from my image</span>
                  </label>
                </div>
                {tiktokGenerating && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-[10px] text-amber-600">Generating TikTok video...</span>
                  </div>
                )}
                {tiktokUploading && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="w-3 h-3 border-2 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                    <span className="text-[10px] text-[#00aef7]">Uploading video...</span>
                  </div>
                )}
                {tiktokError && <p className="text-[10px] text-red-500 mt-1">{tiktokError}</p>}
                {tiktokVideoAssetId && !tiktokGenerating && !tiktokUploading && (
                  <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    TikTok video ready
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── Footer buttons ─── */}
        <div className="shrink-0 bg-white border-t border-gray-200 px-3 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onClose(tiktokVideoAssetId)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" /></svg>
            Back to Editing
          </button>

          <button
            type="button"
            onClick={() => {
              if (isImageAsset && !effectiveTiktokAssetId) {
                setShowNoVideoWarning(true);
              } else {
                onPublish(effectiveTiktokAssetId, editedCaptions, editedOverlays);
              }
            }}
            className="px-3 py-1.5 rounded-lg text-white text-xs font-medium shadow-sm flex items-center gap-1 transition-colors bg-[#00aef7] hover:bg-[#0097d6]"
          >
            Create your landing page
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" /></svg>
          </button>
        </div>

        {/* ─── No video warning dialog ─── */}
        {showNoVideoWarning && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-2xl">
            <div className="bg-white rounded-xl shadow-xl p-5 mx-4 max-w-[320px]">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">No video generated</p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    TikTok and YouTube require a video version of your design. Without it, this creation won't be available for those platforms in the Sharing Center.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowNoVideoWarning(false)}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={() => { setShowNoVideoWarning(false); onPublish(null, editedCaptions, editedOverlays); }}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-[#00aef7] text-white text-xs font-medium hover:bg-[#0097d6] transition-colors"
                >
                  Publish Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
